import React, { useEffect, useMemo } from 'react';
import classNames from 'classnames/bind';
import { debounce } from 'lodash';
import { useResizeObserver } from 'usehooks-ts';

import type { ConditionRating } from 'types/conditionRating';
import type { SwellsTimestep } from 'types/swell';
import useSetupAdvancedSwellGraph from '../../hooks/useSetupAdvancedSwellGraph';

import styles from './ForecastGraphAdvancedSwellContainer.module.scss';

const cx = classNames.bind(styles);

export interface ForecastGraphAdvancedSwellContainerProps {
  activeItem?: {
    condition: ConditionRating;
    day: number;
    hour: number;
    timestamp?: number;
    utcOffset?: number;
  };
  activeSwell: number;
  data: Array<SwellsTimestep>;
  hoveredSwell: number;
  isEnergy: boolean;
  numberOfTicks: number;
  onHoveredSwell: (index: number) => void;
  onSelectSwell: (index: number) => void;
  overallMaxHeight: number;
  setTooltipTopPosition: (topPosition: number) => void;
  units: string;
}

const ForecastGraphAdvancedSwellContainer: React.FC<ForecastGraphAdvancedSwellContainerProps> = ({
  activeItem,
  activeSwell,
  data,
  hoveredSwell,
  isEnergy,
  numberOfTicks,
  onHoveredSwell,
  onSelectSwell,
  overallMaxHeight,
  setTooltipTopPosition,
  units,
}) => {
  const canvasRef = React.useRef<HTMLDivElement | null>(null);

  const { height: graphHeight = 100, width: graphWidth = 320 } = useResizeObserver({
    ref: canvasRef,
  });

  const { setupAdvancedSwellGraph } = useSetupAdvancedSwellGraph({
    activeItem,
    activeSwell,
    data,
    graphHeight,
    graphWidth,
    hoveredSwell,
    isEnergy,
    numberOfTicks,
    onHoveredSwell,
    onSelectSwell,
    overallMaxHeight,
    setTooltipTopPosition,
    units,
  });

  const debouncedSetupAdvancedSwellGraph = useMemo(
    () => debounce(setupAdvancedSwellGraph, 100, { leading: true, trailing: true }),
    [setupAdvancedSwellGraph],
  );

  useEffect(() => {
    if (graphWidth) debouncedSetupAdvancedSwellGraph();

    return () => {
      debouncedSetupAdvancedSwellGraph.cancel();
    };
  }, [graphWidth, debouncedSetupAdvancedSwellGraph]);

  return (
    <div className={cx(['advanced-swell-graph', styles.wrapper])}>
      <div className={cx(['advanced-swell-graph-canvas', styles.canvas])} ref={canvasRef} />
    </div>
  );
};

export default ForecastGraphAdvancedSwellContainer;
